import React, { useState, useEffect, useMemo } from 'react';
import { FiArrowLeft, FiPlus, FiEdit2, FiTrash2, FiSearch, FiCamera } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import './ManageProducts.css';

const UNITS = ['piece', 'pack', 'box', 'ream', 'set'];

const ManageProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm]   = useState({});
  const [saving, setSaving]       = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      let data;
      try {
        const res = await api.get('/admin/products?limit=200');
        data = res.data;
      } catch {
        const res = await api.get('/products?limit=200&all=true');
        data = res.data;
      }
      setProducts(data.products || []);
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (product) => {
    const nextState = !product.isActive;
    setProducts(prev => prev.map(p => p._id === product._id ? { ...p, isActive: nextState } : p));
    try {
      await api.put(`/admin/products/${product._id}`, { isActive: nextState });
      toast.success(`${product.name} marked as ${nextState ? 'Active' : 'Inactive'}`);
    } catch {
      toast.error('Failed to update status');
      fetchProducts();
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/products/categories/all');
      setCategories(data.categories || []);
    } catch {}
  };

  const startEdit = (product) => {
    setEditingId(product._id);
    setEditForm({
      name:        product.name        || '',
      description: product.description || '',
      category:    product.category?._id || product.category || '',
      price:       product.price       ?? '',
      mrp:         product.mrp         ?? '',
      stock:       product.stock       ?? '',
      unit:        product.unit        || 'piece',
      image:       product.image       || '',
      brand:       product.brand       || '',
      tags:        (product.tags || []).join(', '),
      isActive:    product.isActive    ?? true,
      isFeatured:  product.isFeatured  ?? false,
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPG, PNG and WebP images are allowed');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('files', file);

      const { data } = await api.post('/upload?folder=product', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });

      const uploaded = data.files?.[0];
      if (!uploaded) throw new Error('Upload failed');

      setEditForm(prev => ({ ...prev, image: uploaded.viewLink }));
      toast.success('Product image uploaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const saveEdit = async (id) => {
    if (!editForm.name || editForm.price === '') {
      toast.error('Name and price are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        price: Number(editForm.price),
        mrp:   editForm.mrp !== '' ? Number(editForm.mrp) : undefined,
        stock: Number(editForm.stock),
        tags:  editForm.tags ? editForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      await api.put(`/admin/products/${id}`, payload);
      toast.success('Product updated');
      setEditingId(null);
      fetchProducts();
    } catch {
      toast.error('Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch {
      toast.error('Failed to delete product');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p =>
      p.name?.toLowerCase().includes(q) ||
      p.brand?.toLowerCase().includes(q) ||
      (p.category?.name || '').toLowerCase().includes(q)
    );
  }, [products, search]);

  if (loading) {
    return <div className="page-container"><div className="loading-spinner" /></div>;
  }

  return (
    <div className="page-container">
      <div className="page-top-bar">
        <button className="back-btn" onClick={() => navigate('/admin')}><FiArrowLeft /></button>
        <h1>Manage Products</h1>
        <button className="add-btn" onClick={() => navigate('/admin/add-product')}><FiPlus /></button>
      </div>

      <div className="mp-search-wrap">
        <FiSearch className="mp-search-icon" />
        <input
          className="mp-search-input"
          type="text"
          placeholder="Search by name, brand or category…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button className="mp-search-clear" onClick={() => setSearch('')}>✕</button>
        )}
      </div>

      <div className="orders-count">
        {filtered.length}{search ? ` of ${products.length}` : ''} product{filtered.length !== 1 ? 's' : ''}
      </div>

      <div className="products-admin-list">
        {filtered.length === 0 ? (
          <div className="mp-no-results">No products match "{search}"</div>
        ) : filtered.map(product => (
          <div className={`product-admin-card${editingId === product._id ? ' editing' : ''}`} key={product._id}>
            {editingId === product._id ? (

              /* ── Full edit form ── */
              <div className="product-edit-form">

                {/* Product Image Uploader */}
                <div className="pef-group">
                  <label>Product Image</label>
                  <div className="image-upload-container">
                    {editForm.image ? (
                      <div className="image-preview-box">
                        <img src={editForm.image} alt="Product Preview" className="product-image-thumbnail" />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => setEditForm(prev => ({ ...prev, image: '' }))}
                        >
                          <FiTrash2 /> Remove
                        </button>
                      </div>
                    ) : (
                      <label className="image-upload-placeholder">
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                          disabled={uploadingImage}
                        />
                        {uploadingImage ? (
                          <div className="loader-small">Uploading to Cloudinary...</div>
                        ) : (
                          <>
                            <FiCamera className="upload-icon" />
                            <span>Upload Product Image</span>
                          </>
                        )}
                      </label>
                    )}
                  </div>
                </div>

                <div className="pef-group">
                  <label>Product Name *</label>
                  <input type="text" name="name" value={editForm.name} onChange={handleChange} placeholder="Product name" />
                </div>

                <div className="pef-group">
                  <label>Description</label>
                  <textarea name="description" value={editForm.description} onChange={handleChange} placeholder="Product description..." rows={2} />
                </div>

                <div className="pef-group">
                  <label>Category</label>
                  <select name="category" value={editForm.category} onChange={handleChange}>
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div className="pef-row">
                  <div className="pef-group">
                    <label>Price (₹) *</label>
                    <input type="number" name="price" value={editForm.price} onChange={handleChange} placeholder="0" min="0" />
                  </div>
                  <div className="pef-group">
                    <label>MRP (₹)</label>
                    <input type="number" name="mrp" value={editForm.mrp} onChange={handleChange} placeholder="0" min="0" />
                  </div>
                </div>

                <div className="pef-row">
                  <div className="pef-group">
                    <label>Stock *</label>
                    <input type="number" name="stock" value={editForm.stock} onChange={handleChange} placeholder="0" min="0" />
                  </div>
                  <div className="pef-group">
                    <label>Unit</label>
                    <select name="unit" value={editForm.unit} onChange={handleChange}>
                      {UNITS.map(u => <option key={u} value={u}>{u.charAt(0).toUpperCase() + u.slice(1)}</option>)}
                    </select>
                  </div>
                </div>

                <div className="pef-row">
                  <div className="pef-group">
                    <label>Brand</label>
                    <input type="text" name="brand" value={editForm.brand} onChange={handleChange} placeholder="e.g. Classmate" />
                  </div>
                </div>

                <div className="pef-group">
                  <label>Tags (comma separated)</label>
                  <input type="text" name="tags" value={editForm.tags} onChange={handleChange} placeholder="notebook, school, writing" />
                </div>

                <div className="pef-checks">
                  <label className="pef-check-label">
                    <input type="checkbox" name="isActive" checked={editForm.isActive} onChange={handleChange} />
                    Active
                  </label>
                  <label className="pef-check-label">
                    <input type="checkbox" name="isFeatured" checked={editForm.isFeatured} onChange={handleChange} />
                    Featured
                  </label>
                </div>

                <div className="edit-actions">
                  <button className="save-btn" onClick={() => saveEdit(product._id)} disabled={saving || uploadingImage}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button className="cancel-edit-btn" onClick={() => setEditingId(null)}>Cancel</button>
                </div>
              </div>

            ) : (
              /* ── Card view ── */
              <>
                <div className="pa-left">
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="pa-img"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="pa-img-placeholder" style={{ display: product.image ? 'none' : 'flex' }}>📦</div>
                </div>
                <div className="pa-info">
                  <div className="pa-name">{product.name}</div>
                  <div className="pa-meta">
                    <span>₹{product.price}{product.mrp > product.price ? ` / ₹${product.mrp}` : ''}</span>
                    <span>Stock: {product.stock}</span>
                    <button
                      type="button"
                      className={`pa-status ${product.isActive ? 'active' : 'inactive'}`}
                      onClick={() => toggleActive(product)}
                      title={`Click to ${product.isActive ? 'deactivate' : 'activate'}`}
                      style={{ cursor: 'pointer', border: 'none', background: 'transparent', padding: '2px 6px', borderRadius: '4px' }}
                    >
                      {product.isActive ? '● Active' : '● Inactive'}
                    </button>
                  </div>
                </div>
                <div className="pa-actions">
                  <button className="pa-edit" onClick={() => startEdit(product)}><FiEdit2 /></button>
                  <button className="pa-delete" onClick={() => deleteProduct(product._id)}><FiTrash2 /></button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ManageProducts;

